/**
 * Login / register / register-prompt overlay logic.
 *
 * The overlays themselves live in `index.html` (mockup-verbatim markup).
 * This module wires the open/close lifecycle, form submission, and the
 * cross-links between them ("Ei vielä tunnusta? Rekisteröidy tästä").
 */
import { login, register } from '../api/auth.ts';
import { APIRequestError } from '../api/client.ts';
import { currentUser } from '../state/session.ts';

type AfterLogin = () => void;

let afterLogin: AfterLogin = () => {};

export function setAfterLogin(cb: AfterLogin): void {
  afterLogin = cb;
}

function show(id: string): void {
  document.getElementById(id)!.style.display = 'flex';
}
function hide(id: string): void {
  document.getElementById(id)!.style.display = 'none';
}

export function showLoginScreen(): void {
  hide('registerScreen');
  hide('registerPrompt');
  show('loginScreen');
  document.getElementById('loginEmail')?.focus();
}

export function showRegisterScreen(): void {
  hide('loginScreen');
  hide('registerPrompt');
  show('registerScreen');
  document.getElementById('regFirstName')?.focus();
}

export function showRegisterPrompt(): void {
  // Anonymous users that click an estate are nudged to sign up.
  if (currentUser()) return; // safety: don't show to authenticated users
  show('registerPrompt');
}

export function hideAllOverlays(): void {
  hide('loginScreen');
  hide('registerScreen');
  hide('registerPrompt');
}

export function bindAuthModals(): void {
  // Header buttons.
  document.getElementById('navLoginBtn')!.addEventListener('click', showLoginScreen);
  document.getElementById('navRegisterBtn')!.addEventListener('click', showRegisterScreen);

  // Close buttons.
  document.getElementById('loginCloseBtn')!.addEventListener('click', () => hide('loginScreen'));
  document.getElementById('registerCloseBtn')!.addEventListener('click', () => hide('registerScreen'));
  document.getElementById('registerPromptCloseBtn')!.addEventListener('click', () => hide('registerPrompt'));

  // Cross-links.
  document.getElementById('loginToRegisterLink')!.addEventListener('click', (e) => {
    e.preventDefault();
    showRegisterScreen();
  });
  document.getElementById('registerToLoginLink')!.addEventListener('click', (e) => {
    e.preventDefault();
    showLoginScreen();
  });
  document.getElementById('promptToLoginBtn')!.addEventListener('click', () => {
    hide('registerPrompt');
    showLoginScreen();
  });
  document.getElementById('promptToRegisterBtn')!.addEventListener('click', () => {
    hide('registerPrompt');
    showRegisterScreen();
  });

  // Click outside the card closes the modal — matches the mockup's UX.
  for (const id of ['loginScreen', 'registerScreen', 'registerPrompt']) {
    const overlay = document.getElementById(id)!;
    overlay.addEventListener('click', (e) => {
      if (e.target === overlay) hide(id);
    });
  }

  // Forms.
  bindLoginForm();
  bindRegisterForm();
}

function bindLoginForm(): void {
  const form = document.getElementById('loginForm') as HTMLFormElement;
  const emailEl = document.getElementById('loginEmail') as HTMLInputElement;
  const passEl = document.getElementById('loginPassword') as HTMLInputElement;
  const errEl = document.getElementById('loginError')!;
  const submit = document.getElementById('loginSubmit') as HTMLButtonElement;

  form.addEventListener('submit', async (ev) => {
    ev.preventDefault();
    errEl.style.display = 'none';

    const email = emailEl.value.trim();
    const password = passEl.value;
    if (!email || !password) {
      errEl.textContent = 'Anna sähköposti ja salasana.';
      errEl.style.display = 'block';
      return;
    }
    submit.disabled = true;
    submit.textContent = 'Kirjaudutaan…';
    try {
      await login({ email, password });
      hideAllOverlays();
      passEl.value = '';
      afterLogin();
    } catch (e) {
      if (e instanceof APIRequestError) {
        if (e.body.code === 'pending_approval') {
          errEl.textContent = 'Tunnuksesi odottaa vielä admin-hyväksyntää.';
        } else if (e.body.code === 'account_suspended') {
          errEl.textContent = 'Tunnus on jäädytetty. Ota yhteyttä ylläpitoon.';
        } else if (e.status === 401) {
          errEl.textContent = 'Sähköposti tai salasana on virheellinen.';
        } else {
          errEl.textContent = e.body.error || 'Kirjautuminen epäonnistui.';
        }
      } else {
        errEl.textContent = 'Verkkovirhe. Kokeile uudelleen.';
      }
      errEl.style.display = 'block';
    } finally {
      submit.disabled = false;
      submit.textContent = 'Kirjaudu sisään';
    }
  });
}

function bindRegisterForm(): void {
  const form = document.getElementById('registerForm') as HTMLFormElement;
  const errEl = document.getElementById('regError')!;
  const okEl = document.getElementById('regSuccess')!;
  const submit = document.getElementById('regSubmitBtn') as HTMLButtonElement;

  form.addEventListener('submit', async (ev) => {
    ev.preventDefault();
    errEl.style.display = 'none';
    okEl.style.display = 'none';

    const first = (document.getElementById('regFirstName') as HTMLInputElement).value.trim();
    const last = (document.getElementById('regLastName') as HTMLInputElement).value.trim();
    const email = (document.getElementById('regEmail') as HTMLInputElement).value.trim();
    const pw = (document.getElementById('regPassword') as HTMLInputElement).value;
    const pw2 = (document.getElementById('regPassword2') as HTMLInputElement).value;
    const terms = (document.getElementById('regAcceptTerms') as HTMLInputElement).checked;

    if (!first || !last || !email || !pw) {
      errEl.textContent = 'Täytä kaikki pakolliset kentät.';
      errEl.style.display = 'block';
      return;
    }
    if (pw.length < 8) {
      errEl.textContent = 'Salasanan tulee olla vähintään 8 merkkiä.';
      errEl.style.display = 'block';
      return;
    }
    if (pw !== pw2) {
      errEl.textContent = 'Salasanat eivät täsmää.';
      errEl.style.display = 'block';
      return;
    }
    if (!terms) {
      errEl.textContent = 'Hyväksy ehdot ja GDPR-suostumus.';
      errEl.style.display = 'block';
      return;
    }

    submit.disabled = true;
    submit.textContent = 'Lähetetään…';
    try {
      await register({
        email,
        password: pw,
        first_name: first,
        last_name: last,
        gdpr_consent: '"accepted"',
      });
      okEl.textContent = 'Rekisteröityminen onnistui. Admin tarkistaa tunnuksesi ja saat sähköpostin kun se on hyväksytty.';
      okEl.style.display = 'block';
      form.reset();
    } catch (e) {
      if (e instanceof APIRequestError) {
        errEl.textContent = e.body.error || 'Rekisteröityminen epäonnistui.';
      } else {
        errEl.textContent = 'Verkkovirhe. Kokeile uudelleen.';
      }
      errEl.style.display = 'block';
    } finally {
      submit.disabled = false;
      submit.textContent = 'Rekisteröidy';
    }
  });
}
